import React, { Suspense, useEffect, useRef, useState } from "react";
import { Typography, MenuItem, Select, IconButton, CircularProgress } from "@material-ui/core";
import { Remove } from "@material-ui/icons";
import AddIcon from "@material-ui/icons/Add";
import { getServerUri } from "../modules/uriUtility";
import _ from "lodash";
import DialogGlbDelete from "./dialogGlbDelete";
import { makeStyles } from "@material-ui/core/styles";

const DialogGlbOptimizer = React.lazy(() => import("./accountSettingsComponents/dialogGlbOptimizer"));

const useStyles = makeStyles({
  iconButton: { padding: 0 }
});

/**
 * @param props
 * @param props.glbs
 * @param props.selectedGLB
 * @param props.classes
 * @param props.setValue
 * @param props.path
 * @param props.destination
 * @param props.bucket
 * @param props.componentType
 * @param props.insertGlbPathValue
 * @param props.componentID
 * @param props.selectedBase
 * @param props.userPermissions
 * @param props.createSnackbar
 * @param props.deleteGlbFromGlbs
 * @param props.selectedVersion
 * @param props.maxSizeMb - upload size cap in MB (default 3)
 * @returns {JSX.Element|null}
 */
export default function selectorGlb(props) {
  const [glbs, setGlbs] = useState(GlbsToView(_.get(props, "glbs", [])));
  const defaultGlbID = String(_.get(glbs, "[0]._id", ""));
  const [selectedGlbID, setSelectedGlbID] = useState(_.get(props, "selectedGlb._id", defaultGlbID));
  const [isDeleteGlbDialogOpen, setIsDeleteGlbDialogOpen] = useState(false);
  const [glbToDelete, setGlbToDelete] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isOptimizerOpen, setIsOptimizerOpen] = useState(false);
  const uploadLock = useRef(false);
  const classes = useStyles();

  useEffect(() => {
    if (isSelectionValid(_.get(props, "selectedGlb._id", ""))) {
      setSelectedGlbID(_.get(props, "selectedGlb._id", ""));
    } else if (isSelectionValid(defaultGlbID)) {
      selectGlb(defaultGlbID);
    }
  }, [_.get(props, "selectedVersion")]);

  function GlbsToView(glbs) {
    return glbs.filter((glb) => {
      return _.get(glb, "bucket") === _.get(props, "bucket");
    });
  }
  function gettingMargin(numberMargin) {
    if (props.tooltip != undefined) {
      if (numberMargin) {
        return numberMargin.toString() + "px";
      }
    } else return (0).toString() + "px";
  }

  useEffect(() => {
    setGlbs(GlbsToView(_.get(props, "glbs", [])));
  }, [_.get(props, "glbs", []), _.size(props.glbs)]);

  useEffect(() => {
    if (_.size(glbs) > 0 && !isSelectionValid(selectedGlbID)) {
      selectGlb(String(glbs[0]._id));
    }
  }, [glbs]);

  function isSelectionValid(id, newGlbs) {
    let glbs2;
    if (_.isNil(newGlbs)) {
      glbs2 = glbs;
    } else {
      glbs2 = newGlbs;
    }
    const foundGlbs = glbs2.filter((glb) => {
      return String(glb._id) === String(id);
    });
    return foundGlbs.length > 0 || id === "";
  }


  function selectGlb(id, newGlbs) {
    if (!isSelectionValid(id, newGlbs)) return;
    setSelectedGlbID(id);
    let glbs2;
    if (_.isNil(newGlbs)) {
      glbs2 = glbs;
    } else {
      glbs2 = newGlbs;
    }
    const selectedGlbRecord = glbs2.filter((glb) => {
      return String(glb._id) === String(id);
    })[0];
    const newSelectedGlbRecord = _.clone(selectedGlbRecord);
    _.set(newSelectedGlbRecord, "destination", _.get(props, "destination"));
    props.setValue({
      value: [newSelectedGlbRecord]
    });
  }

  const maxSizeMb = Number(_.get(props, "maxSizeMb")) > 0 ? Number(_.get(props, "maxSizeMb")) : 3;

  async function importGlb(file) {
    if (uploadLock.current) throw new Error("An upload is already in progress.");
    if (props.userPermissions["manageAssets"] === false || props.userPermissions["editContent"] === false) {
      throw new Error("You are not authorized to upload or update this model.");
    }
    if (file.size > 1024 * 1024 * maxSizeMb) {
      throw new Error(`File size should be less than ${maxSizeMb} MB. Optimize further or choose another model.`);
    }
    const formData = new FormData();
    formData.append("files", file);
    formData.append("name", file.name);
    formData.append("componentType", _.get(props, "componentType"));
    formData.append("path", _.get(props, "path"));
    formData.append("bucket", _.get(props, "bucket"));
    formData.append("accessToken", localStorage.getItem("session.accessToken"));
    uploadLock.current = true;
    setIsUploading(true);
    try {
      const response = await fetch(getServerUri("glb/uploadGlb"), { method: "POST", body: formData });
      if (response.status !== 200) {
        throw new Error(response.status === 401 || response.status === 403
          ? "You are not authorized to insert, contact your administrator."
          : `GLB upload failed (${response.status}). Please try again.`);
      }
      const data = await response.json();
      const records = _.get(data, "records", []);
      if (!records.length || !records[0]._id) throw new Error("Upload returned no model. Please try again.");
      const newGlbs = [...glbs, ...records];
      setGlbs(newGlbs);
      props.insertGlbPathValue(records);
      selectGlb(String(records[0]._id), newGlbs);
    } finally {
      uploadLock.current = false;
      setIsUploading(false);
    }
  }

  return (
    <div id={`${props.id}.Div`}>
      {" "}
      {props.isVisible && (
        <>
          {_.get(props, "label") && (
            <Typography style={{ marginTop: gettingMargin(props.tooltipLength) }} id="discrete-slider" gutterBottom>
              {_.get(props, "label")}
            </Typography>
          )}
          <div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "flex-start" }}>
            <Select
              onChange={(event) => {
                if (props.userPermissions["editContent"] === false) {
                  props.createSnackbar(
                    "You do not have permission to update, contact your administrator.",
                    "warning",
                    false
                  );
                  return;
                }
                if (isSelectionValid(String(event.target.value))) {
                  selectGlb(String(event.target.value));
                }
              }}
              className={props.classes.imageSelector}
              value={String(selectedGlbID)}
            >
              {glbs.map((glb) => (
                <MenuItem
                  key={String(glb._id)}
                  value={String(glb._id)}
                  style={{
                    display: "flex",
                    justifyContent: "space-between"
                  }}
                >
                  <Typography id="discrete-slider" gutterBottom>
                    {glb.name}
                  </Typography>
                  {selectedGlbID !== glb._id && (
                    <div
                      style={{
                        float: "right"
                      }}
                    >
                      <IconButton
                        className={classes.iconButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (props.userPermissions["deleteContent"] === false) {
                            props.createSnackbar(
                              "You do not have permission to delete, contact your administrator.",
                              "warning",
                              false
                            );
                            return;
                          }
                          setGlbToDelete(glb);
                          setIsDeleteGlbDialogOpen(true);
                        }}
                      >
                        <Remove />
                      </IconButton>
                    </div>
                  )}
                </MenuItem>
              ))}
            </Select>
            {isUploading ? (
              <CircularProgress size={24} color="secondary" style={{ margin: "0 4px" }} />
            ) : (
              <IconButton
                id={`${props.id}.uploadButton`}
                className={classes.iconButton}
                onClick={() => {
                  if (props.userPermissions["manageAssets"] === false) {
                    props.createSnackbar(
                      "You are not authorized to insert, contact your administrator.",
                      "warning",
                      false
                    );
                    return;
                  }
                  if (props.userPermissions["editContent"] === false) {
                    props.createSnackbar(
                      "You are not authorized to update, contact your administrator.",
                      "warning",
                      false
                    );
                    return;
                  }
                  setIsOptimizerOpen(true);
                }}
              >
                <AddIcon />
              </IconButton>
            )}
            {isOptimizerOpen && <Suspense fallback={<CircularProgress size={24} />}>
              <DialogGlbOptimizer open onClose={() => setIsOptimizerOpen(false)}
                onImport={importGlb} maxImportSizeMb={maxSizeMb} />
            </Suspense>}
            <DialogGlbDelete
              isOpen={isDeleteGlbDialogOpen}
              setIsOpen={(value) => setIsDeleteGlbDialogOpen(value)}
              createSnackbar={props.createSnackbar}
              glbToDelete={glbToDelete}
              selectedGlbID={selectedGlbID}
              glbs={glbs}
              setGlbs={setGlbs}
              selectGlb={selectGlb}
              deleteGlbFromGlbs={props.deleteGlbFromGlbs}
            />
          </div>
        </>
      )}
    </div>
  );
}
